import { gql, useQuery, ApolloError } from "@apollo/client";
import { formatError, useReportError } from "#common/hooks/useReportError";
import { clientContext } from "#utils/graphql_utils";
import type { PaymentProvider, PaymentBankSource } from "#graphql/supergraphTypes";

/**
 * Capability types supported by payment providers.
 * Maps to the ProviderCapabilityType enum in payments-backend.
 */
export type ProviderCapabilityType =
  | "wire_payments"
  | "ach_payments"
  | "bank_to_customer_statements"
  | "intra_day_reporting";

/**
 * GraphQL query for fetching all providers with their capabilities.
 * This is used to populate the bank source dropdowns in the configuration slideout.
 *
 * Note: This query is manually defined because the codegen cannot run without
 * the full supergraph schema which includes payments-backend.
 */
const GET_ALL_PROVIDERS_WITH_CAPABILITIES = gql`
  query GetAllProvidersWithCapabilities {
    providers {
      id
      externalId
      name
      bankSource
      capabilities {
        providerCapabilityId
        id
        name
      }
    }
  }
`;

/** Capability returned from the providers query */
export interface ProviderCapability {
  providerCapabilityId: number; // Use this for providerCapabilityId in mutations
  id: number; // capability_id (foreign key to capability table)
  name: ProviderCapabilityType;
}

/** Provider returned from the providers query */
export interface ProviderWithCapabilities {
  id: number;
  externalId: string;
  name: PaymentProvider;
  bankSource: PaymentBankSource;
  capabilities: ProviderCapability[];
}

/** Response shape for GetAllProvidersWithCapabilities query */
export interface GetAllProvidersWithCapabilitiesData {
  providers: ProviderWithCapabilities[];
}

export interface UseGetAllProvidersWithCapabilitiesReturn {
  providers: ProviderWithCapabilities[];
  loading: boolean;
  error: ApolloError | undefined;
}

/**
 * Hook that fetches all available payment providers with their capabilities.
 * Used to populate bank source options for Fides and RPA configuration.
 *
 * @example
 * ```tsx
 * const { providers, loading, error } = useGetAllProvidersWithCapabilities();
 *
 * // Filter providers by name
 * const fidesProviders = providers.filter(p => p.name === 'FIDES');
 * const rpaProviders = providers.filter(p => p.name === 'RPA');
 * ```
 */
export const useGetAllProvidersWithCapabilities =
  (): UseGetAllProvidersWithCapabilitiesReturn => {
    const { data, loading, error } = useQuery<GetAllProvidersWithCapabilitiesData>(
      GET_ALL_PROVIDERS_WITH_CAPABILITIES,
      {
        context: clientContext(),
        fetchPolicy: "no-cache",
      },
    );

    useReportError(error ? formatError(error) : null);

    return {
      providers: data?.providers ?? [],
      loading,
      error,
    };
  };
